import { getRetailerIdIssueProposal, useQuery } from '@common/utils';
import { BankIdLogin } from '../components/bank-id-login';
import { bankIdApi } from '../store/bank-id-api';
import { getIamLoginUrl, shouldUseIamFlowForCurrentHost } from '../utils/iam-login-url';
import { sanitizeRetailerId } from '@common/utils';
import { authenticateComplete, changeRetailerId, setRememberRetailerId } from '../store/login/login.actions';
import { logIamFailure, logIamContext } from '@common/iam-login-log';
const {
  useCallback,
  useEffect,
  useRef,
  useState
} = React;
const {
  useDispatch,
  useSelector
} = ReactRedux;
const {
  useHistory
} = ReactRouterDOM;
const logger = svs.core.log.getLogger('welcome:BankIdLoginContainer');
const getErrorMessage = ({
  errorCode,
  isThisDevice,
  retailerId
}) => {
  switch (errorCode) {
    case 16:
      {
        const issueProposal = getRetailerIdIssueProposal(retailerId);
        return `Det gick inte att hitta någon användare med ditt personnummer för detta ombud. ${issueProposal}`;
      }
    case 29:
      return 'Ditt personnummer är kopplat till flera användare för detta ombud, be din ombudsadministratör att ta bort dubletterna.';
    case 41:
      return 'Ett okänt fel inträffade.';
    case 42:
      return 'Ett okänt fel inträffade, försök igen.';
    case 45:
      return 'Det BankID du försöker använda är för gammalt eller spärrat. Använd ett annat BankID eller hämta ett nytt hos din internetbank.';
    case 47:
      return 'Internt tekniskt fel. Försök igen.';
    case 49:
      return 'BankID-appen svarar inte. Kontrollera att den är startad och att du har internetanslutning. Om du inte har något giltigt BankID kan du hämta ett hos din bank. Försök sedan igen.';
    case 50:
      return 'En identifiering eller underskrift för det här personnumret är redan påbörjad. Försök igen.';
    case 51:
      return 'Åtgärden avbruten.';
    case 52:
      return 'Åtgärden avbruten. Försök igen.';
    case 56:
      if (isThisDevice) {
        return 'BankID-appen verkar inte finnas i din dator eller telefon. Installera den och hämta ett BankID hos din internetbank. ' + 'Installera appen från din appbutik eller https://install.bankid.com.';
      }
      return 'Misslyckades att läsa av QR koden. Starta BankID-appen och läs av QR koden. Kontrollera att BankID-appen är uppdaterad. ' + 'Om du inte har BankID-appen måste du installera den och hämta ett BankID hos din internetbank. Installera appen från din appbutik eller https://install.bankid.com.';
    default:
      return 'Ett okänt fel inträffade.';
  }
};
export const BankIdLoginContainer = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const history = useHistory();
  const [isLoginThisDevice, setIsLoginThisDevice] = useState(null);
  const [isCanceled, setIsCanceled] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    isComplete,
    retailerId,
    rememberRetailerId: storeRememberRetailerId,
    iamParams
  } = useSelector(({
    login
  }) => login);
  const isIamFlow = query.get('iam') === '1';
  const isIamLoginEnabled = shouldUseIamFlowForCurrentHost(svs.core.detect.feature('ft-iam-login'));
  const isIamFlowDebugEnabled = svs.core.detect.feature('ft-iam-flow-debug-logs');
  const hasIamRedirectContext = Boolean(iamParams?.redirectUri && iamParams?.state);
  const iamBankIdLoginUrl = getIamLoginUrl('/logga-in/dev-login');
  const [initiateLogin, {
    data: ast,
    error: initiateLoginError,
    isLoading
  }] = bankIdApi.useInitiateLoginMutation();
  const [getAuthenticationStatus, {
    data: statusData = {},
    error: pollError
  }] = bankIdApi.useLazyGetAuthenticateStatusQuery();
  const {
    qrData: statusQrData,
    code,
    jwt,
    session,
    progressStatus
  } = statusData;
  const queryAutoStartToken = query.get('autoStartToken');
  const queryIsLoginThisDevice = query.get('isLoginThisDevice') === 'true';
  const statusPoller = useRef(null);
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    if (isIamLoginEnabled && !hasIamRedirectContext && isIamFlowDebugEnabled && query.get('iamLoginError') !== '1') {
      logIamContext(logger, {
        method: 'bankid',
        stage: 'start',
        retailerId,
        event: 'entry_redirect',
        hasRedirectUri: Boolean(iamParams?.redirectUri),
        hasState: Boolean(iamParams?.state)
      });
    }
  }, [isIamLoginEnabled, hasIamRedirectContext, isIamFlowDebugEnabled, query, retailerId, iamParams]);
  useEffect(() => {
    if (!isIamLoginEnabled) {
      return;
    }
    if (!hasIamRedirectContext) {
      const hasIamLoginError = query.get('iamLoginError') === '1';
      if (!hasIamLoginError) {
        window.location.assign(iamBankIdLoginUrl);
      }
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const hasOidcParamsInUrl = params.has('clientId') || params.has('nonce') || params.has('state') || params.has('redirectUri');
    if (hasOidcParamsInUrl) {
      history.replace({
        pathname: history.location.pathname,
        search: 'iam=1'
      });
    }
  }, [history, hasIamRedirectContext, iamBankIdLoginUrl, iamParams, isIamFlow, isIamLoginEnabled, isIamFlowDebugEnabled, query]);
  useEffect(() => {
    if (!initiateLoginError) {
      return;
    }
    const ssnMessage = getRetailerIdIssueProposal(retailerId) || 'Har du angivit rätt ombudsnummer?';
    const errMsg = navigator.onLine ? ssnMessage : 'Inloggningen kunde inte slutföras på grund av obefintlig internetanslutning.';
    setErrorMessage(errMsg);
  }, [initiateLoginError, retailerId, isIamFlow]);
  useEffect(() => {
    if (!initiateLoginError) {
      return;
    }
    let reason;
    if (initiateLoginError.status === 'FETCH_ERROR') {
      reason = navigator.onLine ? 'network_error' : 'offline';
    }
    logIamFailure(logger, {
      method: 'bankid',
      stage: 'initiate',
      code: initiateLoginError.data?.error?.code,
      httpStatus: initiateLoginError.status,
      reason,
      retailerId,
      online: navigator.onLine,
      isThisDevice: isLoginThisDevice
    });
  }, [initiateLoginError, retailerId, isLoginThisDevice]);
  useEffect(() => {
    if (!isIamFlowDebugEnabled) {
      return;
    }
    try {
      logger.info('[IAM_FLOW_TRACE] Polling effect', {
        isIamFlow,
        hasAutoStartToken: Boolean(queryAutoStartToken),
        hadPoller: Boolean(statusPoller.current)
      });
    } catch {}
  }, [isIamFlowDebugEnabled, isIamFlow, queryAutoStartToken]);
  useEffect(() => {
    if (queryAutoStartToken) {
      statusPoller.current = setInterval(() => getAuthenticationStatus(queryAutoStartToken), 1000);
    } else if (statusPoller.current) {
      clearInterval(statusPoller.current);
      statusPoller.current = null;
    }
    return () => {
      if (statusPoller.current) {
        clearInterval(statusPoller.current);
        statusPoller.current = null;
      }
    };
  }, [getAuthenticationStatus, queryAutoStartToken]);
  useEffect(() => {
    if (!isIamFlowDebugEnabled || progressStatus === undefined) {
      return;
    }
    logger.info('[IAM_FLOW_TRACE] BankID progressStatus changed', {
      progressStatus,
      hasCode: Boolean(code),
      hasJwt: Boolean(jwt),
      hasSession: Boolean(session),
      isHidden: document.hidden
    });
  }, [isIamFlowDebugEnabled, progressStatus, code, jwt, session]);
  useEffect(() => {
    if (!isIamFlowDebugEnabled || !queryAutoStartToken) {
      return undefined;
    }
    const onVisibilityChange = () => {
      logger.info('[IAM_FLOW_TRACE] Visibility changed during BankID polling', {
        visibilityState: document.visibilityState,
        hasPoller: Boolean(statusPoller.current)
      });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isIamFlowDebugEnabled, queryAutoStartToken]);
  useEffect(() => {
    if (!ast) {
      return;
    }
    const params = new URLSearchParams(query.toString());
    params.set('autoStartToken', ast.autoStartToken);
    params.set('isLoginThisDevice', String(isLoginThisDevice));
    history.push({
      search: params.toString()
    });
  }, [ast, history, query, isLoginThisDevice]);
  useEffect(() => {
    if (!isCanceled) {
      return;
    }
    const params = new URLSearchParams(query.toString());
    params.delete('autoStartToken');
    params.delete('isLoginThisDevice');
    history.push({
      search: params.toString()
    });
    setIsCanceled(false);
  }, [history, isCanceled, query]);
  useEffect(() => {
    if (!session) {
      return;
    }
    if (isIamFlow) {
      return;
    }
    dispatch(authenticateComplete({
      isBankIdLogin: true,
      isLoginThisDevice
    }));
  }, [dispatch, isIamFlow, session, isLoginThisDevice]);
  useEffect(() => {
    if (!isIamFlowDebugEnabled) {
      return;
    }
    try {
      const redirectUri = iamParams?.redirectUri;
      const state = iamParams?.state;
      const returnedCode = code || jwt;
      const userAgent = navigator.userAgent;
      const browserDetection = svs.core.detect.browser;
      let browser = 'other';
      if (/EdgiOS|Edg\//.test(userAgent)) {
        browser = 'edge';
      } else if (browserDetection.crios()) {
        browser = 'chrome';
      } else if (browserDetection.firefox()) {
        browser = 'firefox';
      } else if (browserDetection.safari()) {
        browser = 'safari';
      } else if (browserDetection.chrome()) {
        browser = 'chrome';
      }
      logger.info('[IAM_FLOW_TRACE] BankID completion effect', {
        isIamFlow,
        hasRedirected: hasRedirectedRef.current,
        hasCode: Boolean(code),
        hasJwt: Boolean(jwt),
        hasRedirectUri: Boolean(redirectUri),
        hasState: Boolean(state),
        hasRetailerId: retailerId !== undefined && retailerId !== null
      });
      if (returnedCode && (!redirectUri || !state)) {
        logger.info('[IAM_FLOW_TRACE] Got code/jwt but missing IAM redirect context, cannot redirect');
      }
      logger.info('[IAM_FLOW_TRACE] Redirecting to IAM callback', {
        browser,
        isIOS: svs.core.detect.os.iOS(),
        pageHost: window.location.host,
        callbackHost: redirectUri ? new URL(redirectUri, window.location.origin).host : undefined
      });
    } catch {}
  }, [isIamFlowDebugEnabled, isIamFlow, iamParams, code, jwt, retailerId]);
  useEffect(() => {
    const redirectUri = iamParams?.redirectUri;
    const state = iamParams?.state;
    const returnedCode = code || jwt;
    if (!isIamFlow) {
      return;
    }
    if (hasRedirectedRef.current) {
      return;
    }
    if (returnedCode && redirectUri && state) {
      hasRedirectedRef.current = true;
      if (statusPoller.current) {
        clearInterval(statusPoller.current);
        statusPoller.current = null;
      }
      if (retailerId !== undefined && retailerId !== null) {
        const sanitizedRetailerId = sanitizeRetailerId(retailerId);
        if (sanitizedRetailerId) {
          document.cookie = `iam_retailer_id=${sanitizedRetailerId}; Max-Age=300; Path=/; SameSite=Lax`;
        }
      }
      const url = `${redirectUri}?state=${encodeURIComponent(state)}&code=${encodeURIComponent(returnedCode)}`;
      window.location.assign(url);
    }
  }, [isIamFlow, iamParams, code, jwt]);
  const handleBankIdAuthenticateStart = ({
    isThisDevice,
    rememberRetailerId
  }) => {
    setIsLoginThisDevice(isThisDevice);
    if (isIamFlow) {
      const sanitizedRetailerId = sanitizeRetailerId(retailerId);
      if (sanitizedRetailerId) {
        document.cookie = `iam_retailer_id=${sanitizedRetailerId}; Max-Age=300; Path=/; SameSite=Lax`;
      }
    }
    if (isIamFlow) {
      initiateLogin({
        retailerId,
        iamParams
      });
    } else {
      initiateLogin(retailerId);
    }
    dispatch(setRememberRetailerId(rememberRetailerId));
  };
  const handleChangeRetailerId = retailerId => dispatch(changeRetailerId(retailerId));
  const handleCancelLogin = useCallback(() => {
    dispatch(bankIdApi.util.resetApiState());
    setIsLoginThisDevice(null);
    hasRedirectedRef.current = false;
    setIsCanceled(true);
  }, [dispatch]);
  const handleClearErrorMessage = () => setErrorMessage(null);
  useEffect(() => {
    if (!pollError || !isIamFlowDebugEnabled) {
      return;
    }
    try {
      logger.info('[IAM_FLOW_TRACE] BankID poll error', {
        isIamFlow,
        httpStatus: pollError.status,
        errorCode: pollError.data?.error?.code,
        requestId: pollError.data?.error?.requestId,
        lastProgressStatus: progressStatus,
        isHidden: document.hidden
      });
    } catch {}
  }, [pollError, isIamFlowDebugEnabled, isIamFlow, progressStatus]);
  useEffect(() => {
    if (!pollError) {
      return;
    }
    const errorCode = pollError.data?.error?.code;
    let errMsg;
    if (pollError?.status === 'FETCH_ERROR') {
      errMsg = navigator.onLine ? 'Ett nätverksfel inträffade.' : 'Inloggningen kunde inte slutföras på grund av obefintlig internetanslutning.';
    } else {
      errMsg = getErrorMessage({
        errorCode,
        isThisDevice: isLoginThisDevice,
        retailerId
      });
    }
    setErrorMessage(errMsg);
    handleCancelLogin();
  }, [pollError, handleCancelLogin, isLoginThisDevice, retailerId, isIamFlow]);
  useEffect(() => {
    if (!pollError) {
      return;
    }
    let reason;
    if (pollError.status === 'FETCH_ERROR') {
      reason = navigator.onLine ? 'network_error' : 'offline';
    }
    logIamFailure(logger, {
      method: 'bankid',
      stage: 'poll',
      code: pollError.data?.error?.code,
      httpStatus: pollError.status,
      reason,
      retailerId,
      online: navigator.onLine,
      isThisDevice: isLoginThisDevice
    });
  }, [pollError, isLoginThisDevice, retailerId]);
  return <BankIdLogin autoStartToken={queryAutoStartToken} errorMessage={errorMessage} isLoading={isLoading} isLoginFinished={isComplete} isLoginThisDevice={queryIsLoginThisDevice} isRememberRetailerIdChecked={storeRememberRetailerId} onBankIdAuthenticateStart={handleBankIdAuthenticateStart} onCancelLogin={handleCancelLogin} onChangeRetailerId={handleChangeRetailerId} onCloseErrorDialog={handleClearErrorMessage} qrData={statusQrData ?? ast?.qrData} retailerId={retailerId} />;
};